import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle,
  CalendarToday,
  LocationOn,
  Phone,
  Star,
  Info
} from '@mui/icons-material';
import api from '../services/api';

const BookingReview = ({ bookingData, onSubmit, loading }) => {
  const [tierInfo, setTierInfo] = useState(null);
  const [loadingTier, setLoadingTier] = useState(true);

  useEffect(() => {
    fetchTierInfo();
  }, [bookingData.serviceTier]);

  const fetchTierInfo = async () => {
    try {
      const response = await api.get(`/v1/travel/tiers/${bookingData.serviceTier}`);
      setTierInfo(response.data);
    } catch (err) {
      console.error('Error fetching tier info:', err);
    } finally {
      setLoadingTier(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTierColor = (tierCode) => {
    switch (tierCode) {
      case 'DIGITAL':
        return '#1A5EA8';
      case 'CONCIERGE':
        return '#10b981';
      case 'WHITE_GLOVE':
        return '#f59e0b';
      default:
        return '#1A5EA8';
    }
  };

  if (loadingTier) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        {/* Header */}
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 1 }}>
          Review Your Booking
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Please review your trip details before confirming
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Service Tier */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Star sx={{ mr: 1, color: getTierColor(bookingData.serviceTier) }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Service Level
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: getTierColor(bookingData.serviceTier) }}>
                {tierInfo?.tierName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tierInfo?.tierDescription}
              </Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: getTierColor(bookingData.serviceTier) }}>
              ${tierInfo?.basePrice.toFixed(2)}
            </Typography>
          </Box>
          {tierInfo?.featuresIncluded && (
            <List dense>
              {tierInfo.featuresIncluded.slice(0, 3).map((feature, index) => (
                <ListItem key={index} sx={{ py: 0 }}>
                  <CheckCircle sx={{ fontSize: 16, color: getTierColor(bookingData.serviceTier), mr: 1 }} />
                  <ListItemText
                    primary={feature}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
              {tierInfo.featuresIncluded.length > 3 && (
                <ListItem sx={{ py: 0 }}>
                  <Typography variant="caption" color="text.secondary">
                    + {tierInfo.featuresIncluded.length - 3} more features
                  </Typography>
                </ListItem>
              )}
            </List>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Destination */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <LocationOn sx={{ mr: 1, color: '#1A5EA8' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Destination
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {bookingData.trialSiteName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {bookingData.trialSiteAddress && `${bookingData.trialSiteAddress}, `}
                {bookingData.destinationCity}, {bookingData.destinationState}
              </Typography>
            </Grid>
            {bookingData.trialName && (
              <Grid item xs={12}>
                <Alert severity="info" icon={<Info />}>
                  <Typography variant="body2">
                    <strong>Trial:</strong> {bookingData.trialName}
                  </Typography>
                  {bookingData.trialId && (
                    <Typography variant="caption" display="block">
                      {bookingData.trialId}
                    </Typography>
                  )}
                </Alert>
              </Grid>
            )}
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Trip Dates */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CalendarToday sx={{ mr: 1, color: '#1A5EA8' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Trip Dates
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Departure
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formatDate(bookingData.tripStartDate)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Return
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formatDate(bookingData.tripEndDate)}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Contact Information */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Phone sx={{ mr: 1, color: '#1A5EA8' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Contact Information
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Your Phone
              </Typography>
              <Typography variant="body1">
                {bookingData.patientPhone}
              </Typography>
            </Grid>
            {bookingData.emergencyContact && (
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Emergency Contact
                </Typography>
                <Typography variant="body1">
                  {bookingData.emergencyContact}
                  {bookingData.emergencyPhone && ` - ${bookingData.emergencyPhone}`}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Special Requirements */}
        {bookingData.specialRequirements && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Special Requirements
              </Typography>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#f9fafb' }}>
                <Typography variant="body2">
                  {bookingData.specialRequirements}
                </Typography>
              </Paper>
            </Box>
          </>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Total */}
        <Box sx={{ bgcolor: '#f9fafb', p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Total
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: getTierColor(bookingData.serviceTier) }}>
              ${tierInfo?.basePrice.toFixed(2)}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Flight and hotel costs will be added separately based on your selections
          </Typography>
        </Box>

        {/* Important Notice */}
        <Alert severity="warning" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>Next Steps:</strong> After confirming your booking, you'll be able to search and book flights and hotels. 
            Payment will be processed once you complete your travel selections.
          </Typography>
        </Alert>
      </Paper>
    </Container>
  );
};

export default BookingReview;